import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, take } from 'rxjs';

import {
  selectMePermissions,
  selectMePermissionsError,
} from '../Store/rbac-sod/rbac-sod.selectors';
import { FeatureAccessService } from './feature-access.service';
import { PermissionGateService } from './permission-gate.service';

/**
 * Route guard for /spend-explorer. Two gates, in order:
 *
 *  1. Tenant enablement — the `tblPage` row with `Link='spend-explorer'` must be
 *     active, read synchronously from the cached sidebar payload.
 *  2. Caller permission — `spend-explorer.view`, the same key the sidebar entry
 *     is hidden on, so the link and the URL cannot disagree. A tenant that is
 *     not in RBAC mode passes, as it does on the server.
 *
 * `has$` default-denies until the MePermissions slice hydrates, so this waits
 * for the slice to settle (data OR error) before deciding; otherwise every cold
 * navigation would bounce a legitimate user.
 */
export const spendExplorerGuard: CanActivateFn = () => {
  const featureAccess = inject(FeatureAccessService);
  const permissionGate = inject(PermissionGateService);
  const store = inject(Store);
  const router = inject(Router);

  if (!featureAccess.isPageActive('spend-explorer')) {
    // Says which gate refused. The sidebar payload is written at login and cached, so the
    // usual cause is a stale cache rather than a tenant that lacks the page — and without
    // this line the two are indistinguishable from the outside.
    console.warn(
      '[SpendExplorer] blocked: no active tblPage row with Link=\'spend-explorer\' in the cached ' +
        'SideBar payload. Log out and back in to refresh it, or seed the page row for this tenant.',
    );
    router.navigateByUrl('dashboard');
    return false;
  }

  return combineLatest([
    permissionGate.has$('spend-explorer.view'),
    store.select(selectMePermissions),
    store.select(selectMePermissionsError),
  ]).pipe(
    filter(([, me, error]) => me != null || error != null),
    take(1),
    map(([allowed]) => (allowed ? true : router.parseUrl('dashboard'))),
  );
};
